/**
 * メール配信の画面（pages/mail/）の英語の文言。en.ts が `...enMail` で取り込む（ja.mail.ts が同じキーを全部持つことを型で強制）。
 *
 * WHY 別ファイルか: en.ts が 650 行を超えている。メールは法律（フッターの表示義務・同意）に触れる文言が多く、
 * まとめて見直せるようにしておきたい（研究 docs/research/2026-09-27-mail-broadcast.md §5 と食い違わせない）。
 * フッターの文言（mail.footer.*）は Core の確定した全文（apps/server の mailBroadcast/compose.ts の footerLines）と一字一句同じにする
 * （見本と実物が違うと、クリエイターが法律の表示を確かめられない）。Core が文言を変えたらここも直す。
 * Kumo の名前は出さない（D3: 差出人はゲーム名）。
 */
export const enMail = {
  'nav.mail': 'Email',
  'mail.title': 'Email',
  'mail.lead': 'Send news to users who asked for email from this app. Only users who opted in (and confirmed) receive it, and every email carries an unsubscribe link.',
  'mail.statusWhat': 'email status',

  // ---------------------------------------------------------------- 使えないとき
  'mail.kids.title': 'Email is not available for kids apps',
  'mail.kids.body': 'This project is set to {key}. Email can only go to adult users, so it cannot be turned on for this project.',
  'mail.off.title': 'Email is off in {env}',
  'mail.off.body': 'Email is off by default. Once it is on:',
  'mail.off.point.consent': 'Users opt in from inside your app and confirm by email. Until someone does, there is no one to send to.',
  'mail.off.point.adults': 'Only adult users can receive it.',
  'mail.off.point.sender': 'You add your legal name, postal address and contact email once. The law requires them in every email footer.',
  'mail.off.how': 'Turn it on in your local file and push, or use {link}.',
  'mail.off.kidsNote': 'Projects set to audience "kids" cannot use email.',
  'mail.mixedNote': 'This project is set to audience "mixed": users who have not confirmed they are adults are left out of every send.',

  // ---------------------------------------------------------------- 自動停止
  'mail.suspended.title': 'Sending is paused',
  'mail.suspended.reason': 'Reason: {reason}',
  'mail.suspended.reason.complaint_rate': 'too many recipients marked your email as spam (0.1% or more)',
  'mail.suspended.reason.bounce_rate': 'too many addresses did not exist (5% or more)',
  'mail.suspended.reason.unknown': 'paused by the {product} team',
  'mail.suspended.since': 'Since {at}',
  'mail.suspended.body': 'This protects your users and every other project that sends email. Sending resumes only after the {product} team reviews it; contact support to ask for a review. Your scheduled and repeating emails were canceled when sending was paused; they do not come back when sending resumes, so create them again.',

  // ---------------------------------------------------------------- 差出人情報
  'mail.sender.title': 'Sender details',
  'mail.sender.why': 'By law, marketing email must show who sent it and a real postal address. These go into the footer of every email automatically, and users can see them.',
  'mail.sender.shared': 'Shared by all your projects. A P.O. box is fine for the address.',
  'mail.sender.complete': 'Complete',
  'mail.sender.incomplete': 'Required before sending',
  'mail.sender.legalName': 'Legal name',
  'mail.sender.legalNameHint': 'your full name, or your company name',
  'mail.sender.postalAddress': 'Postal address',
  'mail.sender.postalAddressHint': 'P.O. box is OK',
  'mail.sender.contactEmail': 'Contact email',
  'mail.sender.contactEmailHint': 'where users can reach you',
  'mail.sender.save': 'Save sender details',
  'mail.sender.saved': 'Sender details saved.',
  'mail.sender.edit': 'Change',

  // ---------------------------------------------------------------- 入力の誤り
  'mail.problem.required': 'Required.',
  'mail.problem.tooLong': 'Too long (up to {max} characters).',
  'mail.problem.email': 'Enter an email address like name@example.com.',
  'mail.problem.newline': 'The subject must be a single line.',
  'mail.problem.invalidTime': 'That date and time does not exist (for example, Feb 30, or the hour skipped when clocks change).',
  'mail.problem.past': 'Pick a time at least 5 minutes from now.',
  'mail.problem.tooFar': 'Pick a time within the next year.',
  'mail.problem.monthDay': 'For monthly emails, the first send must fall on day 1–28 in UTC (not every month has a 29th–31st). Pick an earlier date, or a time that is still day 1–28 in UTC.',

  // ---------------------------------------------------------------- 書く
  'mail.compose.title': 'Write an email',
  'mail.compose.subject': 'Subject',
  'mail.compose.text': 'Message',
  'mail.compose.textHint': 'plain text',
  'mail.compose.zero': 'No one to send to yet.',
  'mail.compose.submit.now': 'Review and send',
  'mail.compose.submit.schedule': 'Review and schedule',
  'mail.compose.submit.recurring': 'Review and set up',
  'mail.compose.blocked.sender': 'Add your {link} first. Sending stays off until all three are filled in.',
  'mail.compose.blocked.suspended': 'Sending is paused (see above). You can still write, but you cannot send until the {product} team resumes it.',

  // ---------------------------------------------------------------- 見本
  'mail.preview.title': 'Preview',
  'mail.preview.from': 'From',
  'mail.preview.subject': 'Subject',
  'mail.preview.noSubject': '(no subject yet)',
  'mail.preview.noText': '(your message goes here)',
  'mail.preview.footer': 'Footer added automatically',
  'mail.preview.missing.legalName': '(legal name missing)',
  'mail.preview.missing.postalAddress': '(postal address missing)',
  'mail.preview.missing.contactEmail': '(contact email missing)',
  'mail.preview.unsubscribeUrl': 'a personal unsubscribe link for each user',
  'mail.preview.domainPending': 'your app’s sending address',
  'mail.preview.note': 'The footer is added to every email and cannot be removed. Each user gets it in their own language (English or Japanese).',
  // フッターの全文（Core の compose.ts の footerLines の en と同じ）
  'mail.footer.notice': 'This is a promotional email. You are receiving it because you signed up for news from {game}.',
  'mail.footer.sender': 'Sender: {value}',
  'mail.footer.address': 'Address: {value}',
  'mail.footer.contact': 'Contact: {value}',
  'mail.footer.unsubscribe': 'Unsubscribe: {url}',

  // ---------------------------------------------------------------- 宛先
  'mail.audience.title': 'Recipients',
  'mail.audience.language': 'User language',
  'mail.audience.allLanguages': 'All languages',
  'mail.audience.lastPlayed': 'Last played',
  'mail.audience.anyTime': 'Any time',
  'mail.audience.withinDays': { one: 'Within {count} day', other: 'Within the last {count} days' },
  'mail.audience.counting': 'Counting recipients…',
  'mail.audience.unknown': 'Could not count recipients right now.',
  'mail.audience.count': { one: '{n} user will receive this', other: '{n} users will receive this' },
  // BAN 中のプレイヤーには送らない（同意は消さない = BAN を解けばまた届く。Core の統合⑰）
  'mail.audience.who': 'Only users who opted in and confirmed, are adults, and have not unsubscribed. Banned users are skipped (their opt-in is kept). Email addresses are never shown to you.',
  'mail.lang.ja': 'Japanese',
  'mail.lang.en': 'English',

  // ---------------------------------------------------------------- いつ送るか
  'mail.when.title': 'When to send',
  'mail.when.mode.now': 'Now',
  'mail.when.mode.schedule': 'Later',
  'mail.when.mode.recurring': 'Repeat',
  'mail.when.yourTime': 'In your time zone',
  'mail.when.date': 'Date',
  'mail.when.firstDate': 'First send',
  'mail.when.time': 'Time',
  'mail.when.every': 'Repeat',
  'mail.every.day': 'Every day',
  'mail.every.week': 'Every week',
  'mail.every.month': 'Every month',
  'mail.everyLabel.day': 'Every day at {time}',
  'mail.everyLabel.week': 'Every {weekday} at {time}',
  'mail.everyLabel.month': 'Monthly on day {day} at {time}',
  'mail.when.recurringHint.day': 'Sends every day at this time, starting on the first date.',
  'mail.when.recurringHint.week': 'Sends every week on the same weekday as the first date.',
  'mail.when.recurringHint.month': 'Sends every month on the same day as the first date.',
  'mail.when.recurringRule': 'The first email goes out exactly at the first date and time; later ones at the same time of day (kept in UTC). If sending was held up for more than a day, the missed ones are skipped, not sent late.',
  'mail.when.dstNote': 'Repeating emails keep a fixed UTC time, so when your clocks change for daylight saving the local send time moves by an hour.',
  'mail.when.now': 'Right away',
  'mail.when.recurring': '{every} (first: {first})',

  // ---------------------------------------------------------------- 確認
  'mail.confirm.title.now': 'Send this email now?',
  'mail.confirm.title.schedule': 'Schedule this email?',
  'mail.confirm.title.recurring': 'Set up this repeating email?',
  'mail.confirm.go.now': 'Send now',
  'mail.confirm.go.schedule': 'Schedule',
  'mail.confirm.go.recurring': 'Start repeating',
  'mail.confirm.recipients': 'Recipients',
  'mail.confirm.recipientsN': { one: '{count} user', other: '{count} users' },
  'mail.confirm.recipientsUnknown': 'Unknown (counted when it sends)',
  'mail.confirm.subject': 'Subject',
  'mail.confirm.when': 'When',
  'mail.confirm.noUndo': 'Email cannot be unsent once it is delivered.',
  'mail.confirm.cancelLater': 'You can cancel it from the history below until it sends. The recipient count is taken again at send time.',
  'mail.confirm.cost': 'Each email sent is billed at cost from your prepaid balance.',
  'mail.confirm.recurringBalance': 'If your prepaid balance runs out, this repeating email is canceled and does not restart when you add funds; you would create it again.',
  'mail.done.now': 'Sending started. Results appear in the history below as they come in.',
  'mail.done.scheduled': 'Scheduled for {at} (your time).',

  // ---------------------------------------------------------------- 送り始めの 1 日の上限（Core の mail-hardening の warmup・2026-09-27 creator/mail-warmup）
  // 上限・苦情の線の数字の意味（なぜこの数か）は書かない（Core の方針: すり抜けの方法を教えない）。今日の上限・残り・期間の終わりだけ
  'mail.warmup.title': 'Daily limit for new senders',
  'mail.warmup.day': 'Day {day} of {days}',
  'mail.warmup.body': 'For the first {days} days after you start sending, only a limited number of emails go out per day (today {limit}, {remaining} left). Anything over the limit is sent automatically on the following days.',
  'mail.warmup.notStarted': 'For the first {days} days after your first send, only a limited number of emails go out per day ({limit} on the first day). Anything over the limit is sent automatically on the following days.',
  'mail.warmup.ends': 'The limit ends on {at} (your time).',
  'mail.warmup.paused': 'Today\'s limit is reached. Sending continues from {at} (your time).',
  'mail.confirm.warmupOver': 'Some will be sent on later days: {remaining} more can go out today, and the other {over} follow automatically from the next day.',
  'mail.confirm.warmupLater': 'For the first {days} days of sending there is a daily limit. If this email goes over it on the day it sends, the rest follows automatically on the following days.',
  'mail.done.deferred': { one: '{count} email will be sent automatically from {at} (your time), because of the daily limit for new senders.', other: '{count} emails will be sent automatically from {at} (your time), because of the daily limit for new senders.' },
  'mail.done.deferredNoTime': { one: '{count} email will be sent automatically on the next day, because of the daily limit for new senders.', other: '{count} emails will be sent automatically on the next day, because of the daily limit for new senders.' },
  'mail.history.carriedOver': { one: '{count} moved to the next day', other: '{count} moved to the next day' },
  'mail.detail.carriedOver': 'Moved to the next day by the daily limit for new senders: {count}. They are sent automatically from {at} (your time).',
  // 2026-09-27 creator/mail-identity: 払い出しの本人確認が済んでいない間の上限（Core の mail-prelaunch）。期間の終わりが無いので「first N days」と書かない。
  //   identityHint は Money の案の訳（payout setup = 出金のためのもの・お金は要らない、を必ず添える）
  'mail.warmup.identityTitle': 'Daily sending limit',
  'mail.warmup.identityBody': 'Only a limited number of emails go out per day (today {limit}, {remaining} left). Anything over the limit is sent automatically on the following days.',
  'mail.warmup.identityNotStarted': 'Only a limited number of emails go out per day ({limit} a day). Anything over the limit is sent automatically on the following days.',
  'mail.warmup.identityHint': 'To send more per day, complete payout setup (identity verification). Payout setup is for withdrawing your earnings, and it costs nothing to complete.',
  'mail.warmup.identityAction': 'Open payout setup',
  'mail.confirm.identityLater': 'There is a daily sending limit. If this email goes over it on the day it sends, the rest follows automatically on the following days.',
  'mail.done.deferredIdentity': { one: '{count} email will be sent automatically from {at} (your time), because of the daily sending limit.', other: '{count} emails will be sent automatically from {at} (your time), because of the daily sending limit.' },
  'mail.done.deferredNoTimeIdentity': { one: '{count} email will be sent automatically on the next day, because of the daily sending limit.', other: '{count} emails will be sent automatically on the next day, because of the daily sending limit.' },
  'mail.detail.carriedOverIdentity': 'Moved to the next day by the daily sending limit: {count}. They are sent automatically from {at} (your time).',

  // ---------------------------------------------------------------- 履歴
  'mail.history.title': 'Sent and scheduled',
  'mail.history.refresh': 'Refresh',
  'mail.history.empty': 'Nothing sent yet',
  'mail.history.emptyBody': 'Emails you send or schedule show up here with their results.',
  'mail.history.sentOf': '{sent} / {recipients} sent',
  'mail.status.draft': 'Draft',
  'mail.status.scheduled': 'Scheduled',
  'mail.status.sending': 'Sending',
  'mail.status.sent': 'Sent',
  'mail.status.canceled': 'Canceled',
  'mail.status.failed': 'Failed',
  'mail.detail.results': 'Results',
  'mail.detail.recipients': 'Recipients',
  'mail.detail.sent': 'Sent',
  'mail.detail.bounced': 'Bounced',
  'mail.detail.complained': 'Marked as spam',
  'mail.detail.unsubscribed': 'Unsubscribed',
  'mail.detail.failed': 'Could not send',
  // 内訳の canceled には、取り消し・自動停止のほかに 24 時間送れずに期限切れ（Core の expire.ts = expired）になった宛先も入る
  'mail.detail.canceled': 'Not sent (canceled or expired)',
  'mail.detail.pending': 'Waiting to send',
  'mail.detail.lines': 'Sending pauses automatically for all your projects at {complaint} spam reports or {bounce} bounces (over recent sends). Amber means you are halfway there.',
  'mail.detail.recurrence': 'Repeats',
  'mail.detail.nextRun': 'Next send',
  'mail.detail.sendAt': 'Scheduled for',
  'mail.detail.sentAt': 'Sent',
  'mail.detail.audience': 'Recipients',
  'mail.detail.filterLanguage': 'Language: {lang}',
  'mail.detail.noFilter': 'Everyone who opted in',
  'mail.detail.createdAt': 'Created',
  'mail.detail.localTime': 'Times are shown in your time zone.',
  'mail.detail.sendDraft': 'Send now',
  'mail.detail.sendDraftBody': 'This draft goes out to everyone who matches its recipients right now. Email cannot be unsent.',
  'mail.detail.cancel': 'Cancel',
  'mail.detail.cancelTitle': 'Cancel this email?',
  'mail.detail.cancelBody': 'It will not be sent.',
  'mail.detail.cancelRecurringBody': 'No more emails will be sent from this schedule. Ones already sent are not affected.',
  'mail.detail.stop': 'Stop sending',
  'mail.detail.stopTitle': 'Stop this send?',
  'mail.detail.stopBody': 'Emails already delivered stay delivered. The rest will not be sent.',

  // ---------------------------------------------------------------- 送れなかった理由（予約・定期の回。failureReason）
  'mail.failure.sender_incomplete': 'Your sender details were incomplete at send time. Fill them in above, then send again.',
  'mail.failure.balance_due': 'Your prepaid balance ran out. Add funds, then send again.',
  'mail.failure.suspended': 'Sending was paused for your account (too many spam reports or bounces).',
  'mail.failure.feature_disabled': 'Email was turned off for this project at send time.',
  'mail.failure.kids_audience': 'The project is set to audience "kids", so email cannot be sent.',
  'mail.failure.provider_error': 'The mail service refused it. Try again later.',
  'mail.failure.canceled': 'Canceled; nothing more is sent for it.',
  'mail.failure.internal_error': 'Something went wrong on our side. Try again; contact support if it keeps happening.',
  // 統合⑰: 残高切れの定期は取り消し = 入金しても戻らない。ゲームの削除・アカウントの閉鎖でも止まる
  'mail.failure.balance_due_recurring': 'Your prepaid balance ran out, so this repeating email was stopped for good. Adding funds does not restart it; add funds, then create it again.',
  'mail.failure.project_deleted': 'This project was deleted, so nothing more is sent for it.',
  'mail.failure.owner_changed': 'This project was handed over to a new owner, so this scheduled email was stopped. Create it again to send it under your name.',
  'mail.failure.account_closed': 'Your account was closed, so nothing more is sent for it.',

  // ---------------------------------------------------------------- エラー
  'mail.error.balance_due': 'Your prepaid balance is used up, so email cannot be sent. Add funds and try again.',
  'mail.error.kids_audience': 'This project is set to audience "kids". Email can only go to adult users, so it cannot be sent.',
  'mail.error.not_draft': 'Only a draft can be sent now. This email was already sent, scheduled or canceled; refresh the history.',
  'mail.error.not_cancelable': 'This email can no longer be canceled: it has already finished sending or was canceled. Refresh the history.',
  'mail.error.idempotency_key_reused': 'This send was already made with different content. Close the confirmation and review again.',
  // WHY「あなたのせいではない」と書くか: 送信の口（KUMO_BROADCAST_PROVIDER）が無いのはサーバー側の設定待ち。
  //   サーバーの英文をそのまま出すと、日本語の画面に英語が出るうえ、クリエイターが自分の設定を探し回る（2026-09-27 リーダーの目視）
  'mail.error.mail_not_configured': 'Email cannot be sent in this environment yet: the server’s mail setup is still pending on the {product} side. There is nothing to fix on your end.',
  // WHY バイトと文字数の目安を両方書くか: 上限は UTF-8 の 80,000 バイト（Gmail が約 102KB より長いメールを切ってフッターを隠すため）。
  //   日本語は 1 文字 3 バイトなので、文字数だけの目安だと日本語の本文で「まだ入るはず」と迷う（2026-09-27 creator/mail-host）
  'mail.error.body_too_large': 'This email is too long. The body can be up to 80,000 bytes (about 80,000 English letters, or about 26,000 Japanese characters), because longer emails get cut off and the required footer would be hidden. Shorten it and try again.',
  'error.sender_incomplete': 'Add your sender details (legal name, postal address, contact email) before sending. The law requires them in the email footer.',
  'error.mail_suspended': 'Sending email is paused for your account because of too many spam reports or bounces. The {product} team must review it before sending resumes.',

  // ---------------------------------------------------------------- Kumo からのお知らせの欄（KumoNewsCard・概要のページ・2026-09-27 news-optout）
  // この欄はクリエイター向けの画面なので Kumo の名前を出す（D3 の「Kumo の名前を出さない」はプレイヤーに届くメールの話）。
  // 説明は founder の決定（MASTER-PLAN §1c）どおり: プレイヤーが選ぶ・初期はチェックなし・確認メールで確定・クリエイターに費用なし・外すと出ない
  'kumoNews.title': '{product} news box',
  'kumoNews.label': 'Show the "Get news from {product}" box',
  'kumoNews.body': 'On email sign-up screens, {product} draws one more checkbox for its own news (new apps and features). Users choose it themselves: it is unchecked at first and a confirmation email decides. It costs you nothing. If you turn it off, it no longer appears on this project\'s sign-up screens.',
  'kumoNews.adultsNote': 'Only adult users see it; projects for kids never show it.',
  'kumoNews.turnOff': 'Remove',
  'kumoNews.turnOn': 'Show',
  'kumoNews.confirmOffTitle': 'Remove the {product} news box in {env}?',
  'kumoNews.confirmOnTitle': 'Show the {product} news box in {env}?',
  'kumoNews.confirmOffBody': 'This pushes a new config to {env}. Within a few seconds the box stops appearing on this project\'s sign-up screens. Users who already signed up keep getting {product} news until they stop it.',
  'kumoNews.confirmOnBody': 'This pushes a new config to {env}. Within a few seconds the box appears again on this project\'s sign-up screens (unchecked at first).',
  'kumoNews.confirmOff': 'Remove in {env}',
  'kumoNews.confirmOn': 'Show in {env}'
};
